import { Button, Switch, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import {
  PLATFORM_SCOPED_PERMISSION_KEYS,
  formatPermissionLabel,
  getPermissionDescription,
  isPlatformScopedPermissionKey,
} from "const/permissions";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

const permissionKeysFromResponse = (response) => {
  const permissions = response?.data?.data?.permissions || [];
  return permissions
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(isPlatformScopedPermissionKey);
};

export default function PlatformPermissionsSection({ userId, value, onChange }) {
  const isDraftMode = !userId;
  const [selectedKeys, setSelectedKeys] = useState(value || []);

  const platformKeys = useMemo(() => [...PLATFORM_SCOPED_PERMISSION_KEYS], []);

  const resolvedKeys = isDraftMode ? value || [] : selectedKeys;

  const updateKeys = (keys) => {
    if (isDraftMode) {
      onChange?.(keys);
      return;
    }
    setSelectedKeys(keys);
    onChange?.(keys);
  };

  const { isLoading, refetch } = useQuery({
    queryKey: ["platformPermissions", userId],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/users/${userId}/platform-permissions`);
      return response.data;
    },
    enabled: Boolean(userId),
    refetchOnWindowFocus: false,
    onSuccess: (payload) => {
      const keys = permissionKeysFromResponse({ data: payload });
      setSelectedKeys(keys);
      onChange?.(keys);
    },
  });

  useEffect(() => {
    if (isDraftMode) {
      return;
    }
    if (!userId) {
      setSelectedKeys([]);
    }
  }, [userId, isDraftMode]);

  const { isLoading: isSaving, mutate: savePlatformPermissions } = useMutation({
    mutationFn: async (keys) => {
      const response = await axios.put(`${API_URL}/users/${userId}/platform-permissions/name`, {
        permissions: keys,
      });
      return response.data;
    },
    onSuccess: (payload) => {
      toast.success("Platform permissions updated successfully");
      const keys = permissionKeysFromResponse({ data: payload });
      setSelectedKeys(keys);
      onChange?.(keys);
      refetch();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Error updating platform permissions"
      );
    },
  });

  const controlsDisabled = !isDraftMode && (isLoading || isSaving);

  const allSelected =
    platformKeys.length > 0 && platformKeys.every((key) => resolvedKeys.includes(key));

  const handleSelectAll = (event) => {
    updateKeys(event.target.checked ? [...platformKeys] : []);
  };

  const toggleKey = (key) => {
    updateKeys(
      resolvedKeys.includes(key)
        ? resolvedKeys.filter((item) => item !== key)
        : [...resolvedKeys, key]
    );
  };

  const handleSave = () => {
    savePlatformPermissions(selectedKeys);
  };

  return (
    <div className="border rounded-md p-4 bg-app-surface">
      <div className="mb-4">
        <Typography variant="subtitle1" className="font-semibold">
          Platform permissions
        </Typography>
        <Typography variant="body2" className="text-app-text-muted mt-1">
          Cross-organisation access for platform administration. These apply to the user globally,
          not per organisation membership. At least one is required for platform-only accounts to
          sign in.
        </Typography>
      </div>

      <div className="flex justify-between items-center border-b pb-3 mb-3">
        <span className="font-medium">Select all</span>
        <Switch checked={allSelected} onChange={handleSelectAll} disabled={controlsDisabled} />
      </div>

      <div className="space-y-3">
        {platformKeys.map((key) => (
          <div key={key} className="flex justify-between items-start gap-4 text-sm">
            <div>
              <span className="font-medium">{formatPermissionLabel(key)}</span>
              <span className="block text-xs text-app-text-muted font-mono">{key}</span>
              <span className="block text-xs text-app-text-muted mt-1">
                {getPermissionDescription(key)}
              </span>
            </div>
            <Switch
              size="small"
              checked={resolvedKeys.includes(key)}
              onChange={() => toggleKey(key)}
              disabled={controlsDisabled}
            />
          </div>
        ))}
      </div>

      {!isDraftMode && (
        <div className="flex justify-end mt-4">
          <Button variant="contained" onClick={handleSave} disabled={isLoading || isSaving}>
            {isSaving ? "Saving..." : "Save platform permissions"}
          </Button>
        </div>
      )}
    </div>
  );
}
